import { useEffect, useRef, useCallback } from 'react'
import { useInterviewStore } from '@/store/interviewStore'
import { buildApiUrl, getApiHeaders } from '@/config/api'

interface CodingAutoSaveOptions {
  problemId: string
  language?: string
  problemTitle?: string
  intervalMs?: number
  isLocked?: boolean
}

export function useCodingAutoSave({ problemId, language = 'javascript', problemTitle, intervalMs = 10000, isLocked = false }: CodingAutoSaveOptions) {
  const sessionId = useInterviewStore(state => state.sessionId)
  const interviewId = useInterviewStore(state => state.interviewId)
  const codingDataRaw = useInterviewStore(state => state.stageData.coding[problemId])
  const markCodingClean = useInterviewStore(state => state.markCodingClean)
  const getCodingProblemMeta = useInterviewStore(state => state.getCodingProblemMeta)
  const setCodingProblemMeta = useInterviewStore(state => state.setCodingProblemMeta)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastSavedCodeRef = useRef<string>('')
  const isSavingRef = useRef<boolean>(false)

  const saveCodingChallenge = useCallback(async () => {
    if (isLocked) {
      console.log(`🔒 Submission locked for problem ${problemId}; skipping auto-save`)
      return
    }

    if (isSavingRef.current) {
      console.log('🔄 Already saving, skipping...');
      return
    }

    if (!sessionId || !interviewId) {
      console.log(`⚠️ Missing sessionId (${sessionId}) or interviewId (${interviewId}), skipping save`);
      return
    }

    const codingData = codingDataRaw || { code: '', language: null, boilerplate: '', isDirty: false, lastSaved: null }
    let meta = getCodingProblemMeta(problemId)
    const currentCode = codingData.code || ''
    const currentLanguage = codingData.language || language

    console.log(`🔍 Auto-save check for problem ${problemId}:`, {
      hasCode: !!currentCode.trim(),
      codeLength: currentCode.length,
      language: currentLanguage,
      isDirty: codingData.isDirty,
      hasMeta: !!meta,
      problemId: meta?.problemId
    });

    // Skip if no code or code hasn't changed
    if (!currentCode.trim() || currentCode === lastSavedCodeRef.current) {
      console.log('⏭️ Skipping save - no code or unchanged');
      return
    }

    if (!meta?.problemId) {
      try {
        // If metadata is missing but we have a saved boilerplate and language, infer minimal meta
        // For fallback/test problems, use the problemId as the problem_id
        const inferredProblemId = Number.isNaN(Number(problemId)) ? 1 : parseInt(problemId, 10);
        console.log(`📝 Setting inferred metadata for problem ${problemId} with problemId: ${inferredProblemId}`);
        setCodingProblemMeta(problemId, {
          problemId: inferredProblemId,
          assignmentOrder: inferredProblemId,
          assignmentUuid: null,
          starterCodeMap: {}
        })
      } catch (_) {
        // ignore
      }
      meta = getCodingProblemMeta(problemId)
      if (!meta?.problemId) {
        console.warn(`⚠️ Still no problemId after setting meta for ${problemId}, skipping save`);
        return
      }
    }

    try {
      isSavingRef.current = true
      // Public endpoint doesn't require authentication token
      const token = localStorage.getItem('token')
      if (!token) {
        console.log('ℹ️ No auth token found, using public endpoint')
      }

      console.log(`🔄 Auto-saving coding problem ${problemId}...`)

      const requestBody = {
        session_id: sessionId,
        candidate_uuid: 'current-user',
        problem_id: meta.problemId,
        question_id: meta.assignmentUuid || problemId,
        problem_title: problemTitle || `Problem ${problemId}`,
        language: currentLanguage,  // Use the current language from coding data
        code_solution: currentCode,
        save_type: 'auto',
      }
      
      console.log(`📤 Sending save request for problem ${problemId}, language: ${currentLanguage}`)

      const response = await fetch(buildApiUrl(`coding-challenges/${interviewId}/save-coding-challenge`), {
        method: 'POST',
        headers: {
          ...getApiHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorText = await response.text()
        let errorDetail = errorText
        try {
          const errorJson = JSON.parse(errorText)
          errorDetail = errorJson.detail || errorJson.message || errorText
        } catch (e) {
          // Use text as-is if not JSON
        }
        console.error('❌ Coding auto-save failed:', errorDetail)
        
        // Log helpful debugging info
        if (errorDetail.includes('No candidate found')) {
          console.log('💡 Tip: The session/interview needs to exist in the database first')
        } else if (errorDetail.includes('foreign key constraint')) {
          console.log('💡 Tip: The problem_id needs to exist in int_coding_problems table')
        }
        return
      }

      // Update last saved code reference
      lastSavedCodeRef.current = currentCode
      
      // Mark as clean in store
      markCodingClean(problemId)

      console.log(`✅ Auto-saved coding problem ${problemId}`)

    } catch (error) {
      console.error('❌ Coding auto-save error:', error)
    } finally {
      isSavingRef.current = false
    }
  }, [codingDataRaw, getCodingProblemMeta, isLocked, markCodingClean, problemId, problemTitle, sessionId, interviewId, setCodingProblemMeta])

  // Start ONLY periodic auto-save every 10 seconds (no immediate triggers)
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    if (!sessionId || !interviewId) {
      return () => undefined
    }

    if (!isLocked) {
      console.log(`🟢 useCodingAutoSave: Starting periodic save for problem ${problemId}`)
      intervalRef.current = setInterval(() => {
        saveCodingChallenge()
      }, intervalMs)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      console.log(`🟢 useCodingAutoSave: Periodic save stopped for problem ${problemId}`)
    }
  }, [interviewId, intervalMs, isLocked, problemId, saveCodingChallenge, sessionId])

  // Save on component unmount or page visibility change (TEMPORARILY DISABLED FOR DEBUGGING)
  useEffect(() => {
    console.log('🔴 useCodingAutoSave: Visibility/beforeunload listeners DISABLED for debugging')
    return () => {
      console.log('🔴 useCodingAutoSave: cleanup - NO SAVE triggered')
    }
  }, [saveCodingChallenge])

  return {
    saveNow: saveCodingChallenge,
    isSaving: isSavingRef.current
  }
}
